import { createSlice, createSelector } from '@reduxjs/toolkit'

class ApiError extends Error {
  constructor(message, statusCode) {
    super(message);
    this.statusCode = statusCode;
  }
}

const maxCashLength = 100;

const initialState = {
  data: [],
  loaded: false,
  loading: false,
  error: null,
  cachedSlug: {},
  cachedTimes: {},
  cachedData: {},
  cachedRoots: {},
}

const getOldestKey = (cachedQueries, cachedTimes) => {
  let time = Date.now()
  let oldestKey

  for (let key in cachedQueries) {
    if (cachedTimes[key] && cachedTimes[key] < time && key !== '/') {
      time = cachedTimes[key]
      oldestKey = key
    }
  }

  return oldestKey
}

const categories = createSlice({
  name: 'categories',
  initialState: initialState,
  reducers: {
    requestCategories(state) {
      state.loading = true
    },
    successCategories(state, { payload }) {
      if (payload) {
        const categories = payload?.categories ?? []
        state.data = categories
        state.loaded = true
      }
      state.loading = false
      state.error = null
    },
    failureCategories(state, { payload }) {
      state.loading = false
      state.error = payload
    },
    requestSlug(state, { payload }) {
      const { slug } = payload || {}

      state.loading = true
      state.error = null
      if (slug) {
        state.cachedSlug[slug] = { ...state.cachedSlug[slug], loading: true, error: null }
      }
    },
    successSlug(state, { payload }) {
      const { slug, response, refresh } = payload
      const { cachedSlug, cachedTimes } = state

      if (response && (!cachedSlug[slug]?.result || cachedSlug[slug]?.loading || refresh)) {
        const cachedSlugLength = Object.keys(cachedSlug).length

        if (cachedSlugLength >= maxCashLength) {
          const oldestKey = getOldestKey(cachedSlug, cachedTimes)

          delete cachedSlug[oldestKey]
          delete cachedTimes[oldestKey]
        }

        cachedSlug[slug] = response || cachedSlug[slug]
        cachedSlug[slug].loading = false
        cachedSlug[slug].error = null
      }

      cachedTimes[slug] = Date.now()

      state.loading = false
    },
    failureSlug(state, { payload }) {
      const { slug, error } = payload

      state.loading = false

      state.cachedSlug[slug] = { ...state.cachedSlug[slug], loading: false, error }
    },
    successData(state, { payload }) {
      const { response, refresh } = payload

      if (!state.cachedData.data || refresh) {
        state.cachedData = response
      }
    },
    requestRoots(state) {
      if (state.cachedRoots?.error) {
        state.cachedRoots = initialState.cachedRoots
      }
    },
    successRoots(state, { payload }) {
      state.cachedRoots = payload.response
    },
    failureRoots(state, { payload }) {
      state.cachedRoots = { error: payload?.error || null }
    },
  },
})

export const getCategoryTreeBySlug = ({ slug, refresh = false, onSuccess, onError, originalError = false }) => async (dispatch, getState, { api }) => {
  const state = getState().categories

  try {
    if (state.cachedSlug[slug]?.result && !refresh) {
      dispatch(successSlug({ slug }))
      if (onSuccess) onSuccess(state.cachedSlug[slug])
    } else {
      dispatch(requestSlug({ slug }))

      const response = await api.categoryTreeBySlug({ slug, version: 1 })
      const result = response?.result

      if (result === 'success') {
        dispatch(successSlug({ slug, response, refresh }))
        if (onSuccess) onSuccess(response)
      } else {
        if (onError) {
          if (originalError || response.data) {
            throw response
          }
          throw new ApiError('Page Not Found', 404)
        }
      }
    }
  } catch (err) {
    console.error(err)
    onError?.(err)
    dispatch(failureSlug({ slug, error: err } ))
  }
}

export const getCategoriesTree = ({ refresh = false, onSuccess, onError }) => async (dispatch, getState, { api }) => {
  const state = getState().categories

  try {
    if (state.cachedData.data && !refresh) {
      if (onSuccess) onSuccess(state.cachedData)
    } else {
      const response = await api.categoryTree()
      const result = response?.result

      if (result === 'success') {
        dispatch(successData({ response, refresh }))
        if (onSuccess) onSuccess(response)
      } else {
        console.error('getCategoryTree failed')
        if (onError) onError()
      }
    }
  } catch (err) {
    console.error(err)
  }
}

export const getCategoriesTreeRoots = ({ refresh = false, onSuccess, onError } = {}) => async (dispatch, getState, { api }) => {
  const state = getState().categories

  try {
    if (state.cachedRoots?.data?.length && !refresh) {
      onSuccess?.(state.cachedRoots)
    } else {
      dispatch(requestRoots())

      const response = await api.categoryTreeRoots()

      if (response?.result === 'success') {
        dispatch(successRoots({ response, refresh }))
        onSuccess?.(response)
      } else {
        console.error('getCategoriesTreeRoots failed')
        dispatch(failureRoots({ error: response }))
        onError?.(response)
      }
    }
  } catch (err) {
    console.error(err)
    onError?.(err)
  }
}

export const getCategories = createSelector(
  state => state.categories,
  categories => {
    const { data } = categories
    return {
      categories: data,
    }
  },
)

export const getLoading = createSelector(
  state => state.categories,
  categories => {
    const { loading } = categories
    return {
      loading,
    }
  },
)

export const selCategoriesRoots = createSelector(
  state => state.categories.cachedRoots,
  cachedRoots => {
    return {
      categoriesRoots: cachedRoots?.data || [],
      error: cachedRoots?.error
    }
  },
)

export const { requestCategories, successCategories, failureCategories, successSlug, failureSlug, successData, requestRoots, successRoots, failureRoots, requestSlug } = categories.actions

export default categories.reducer
